import { useEffect, useImperativeHandle, useRef } from 'react';
import { Formik, FormikProps } from 'formik';
import { clsx } from 'clsx';
import { EntityFormGrids } from './components/EntityFormGrids/EntityFormGrids';
import { Banner } from '../banner/Banner';
import { SetupGuideDocs } from '../docs/SetupGuideDocs/SetupGuideDocs';
import type { EntityFormProps } from './EntityForm';
import { ENTITY_FORM_DATA_IDS } from './entityFormDataIds';
import { TemplateForm, TemplateFormState } from '../template-form/interfaces/template-form';
import { EntityFormMetaData } from './interfaces/entity-form';
import { FormattedElapsedTime } from '../shared/components/FormattedElapsedTime';
import { useEntityForm } from './hooks/useEntityForm';
import styles from './EntityForm.module.scss';

const dataIds = ENTITY_FORM_DATA_IDS;

export function AsyncEntityForm({
  onComplete,
  entity,
  formSubmitResolver,
  form,
  mode,
  isSBSDocsAllowed,
  testConnectionResolver,
  testConnectionTaskIdResolver,
  formImperativeRef,
  onStateChange,
  topAdornment,
  bottomAdornment
}: Omit<EntityFormProps, 'formDataResolver'> & {
  form: TemplateForm<EntityFormMetaData>;
  topAdornment?: React.ReactNode;
  bottomAdornment?: React.ReactNode;
}) {
  const formContainerRef = useRef<HTMLDivElement>(null);
  const formikRef = useRef<FormikProps<TemplateFormState> | null>(null);

  const {
    submitForm,
    state,
    isSubmitDisabled,
    inProgressBannerContent,
    showInProgressBanner,
    showErrorBanner,
    errorBannerCopy,
    isValidating,
    validationSchema,
    showElapsedTime
  } = useEntityForm({
    testConnectionResolver,
    entity,
    formContainerRef,
    testConnectionTaskIdResolver,
    formSubmitResolver,
    onComplete,
    formikRef,
    form,
    mode
  });

  useImperativeHandle(formImperativeRef, () => ({
    submitForm
  }));

  useEffect(() => {
    if (onStateChange) {
      onStateChange({ ...state, isValidating });
    }
  }, [state, isValidating]);

  return (
    <>
      <Formik
        initialValues={form.initialValues}
        onSubmit={() => {}}
        validationSchema={validationSchema}
        innerRef={formikRef}
      >
        {formikProps => {
          return (
            <form
              className='flex-col align-items-center w-100'
              noValidate
              onSubmit={formikProps.handleSubmit}
              data-id={dataIds.form}
            >
              <div
                ref={formContainerRef}
                className='flex-col align-items-center flex-1 scrollable-content w-100'
              >
                <div
                  className={clsx(styles.formContentContainer, topAdornment && styles.topAdornment)}
                >
                  {topAdornment}

                  <EntityFormGrids
                    template={form.template}
                    entity={entity}
                    formDisabled={isSubmitDisabled}
                  />

                  {showErrorBanner ? (
                    <Banner
                      className='mt-7'
                      open
                      dataId={dataIds.errorBanner}
                      color='error'
                      icon='error-outline'
                    >
                      {errorBannerCopy}
                    </Banner>
                  ) : null}

                  {showInProgressBanner ? (
                    <Banner
                      className='mt-7'
                      open
                      dataId={dataIds.testConnectionInProgress}
                      color='primary'
                      icon='testing'
                      iconClass={styles.testConnectionSpinner}
                    >
                      {inProgressBannerContent}

                      {showElapsedTime ? (
                        <>
                          &nbsp;(
                          <FormattedElapsedTime />)
                        </>
                      ) : null}
                    </Banner>
                  ) : null}

                  {bottomAdornment}
                </div>
              </div>
            </form>
          );
        }}
      </Formik>

      {form.metadata.docLink && form.metadata.canRenderSBSDocs && isSBSDocsAllowed ? (
        <div className='setup-guide-docs-common-container'>
          <SetupGuideDocs
            docLink={form.metadata.docLink}
            isParentLoading={false}
            areTabsRendered
            dataId={dataIds.setupGuideDocs}
          />
        </div>
      ) : null}
    </>
  );
}
